import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageShell from "@/components/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { corporateInput, EmptyState, fmtDate } from "@/components/Shared";
import {
  Plus, Briefcase, Check, X, MapPin, Lightning, Phone, EnvelopeSimple,
  Tag, Calendar, CheckCircle, XCircle, Clock, CaretDown,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const blank = { client_name: "", email: "", phone: "", address: "", service_type: "Comercial", notes: "" };

function OrderCard({ o, onApprove, onReject, onDelete }) {
  const pending = o.status === "pending";
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-sm hover:shadow-md hover:border-slate-300 transition-all relative overflow-hidden" data-testid={`order-card-${o.id}`}>
      {pending && <div className="absolute top-0 left-0 right-0 h-[2px] bg-amber-400" />}

      <div className="p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="min-w-0 flex-1">
            <div className="text-base font-semibold text-slate-900 truncate" title={o.client_name}>{o.client_name}</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold mt-0.5 font-mono">#{o.id.slice(0,8).toUpperCase()}</div>
          </div>
          {o.source === "financial_admin_form" && (
            <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 border border-indigo-200 text-indigo-700 rounded">desde financiero</span>
          )}
        </div>

        <div className="space-y-1.5 text-xs text-slate-600 mb-4">
          {o.address && <div className="flex items-start gap-1.5"><MapPin size={12} weight="regular" className="text-slate-400 mt-0.5 shrink-0" /><span className="line-clamp-2">{o.address}</span></div>}
          {o.previous_address && o.type === "transfer" && (
            <div className="flex items-start gap-1.5 text-amber-700"><MapPin size={12} weight="regular" className="text-amber-500 mt-0.5 shrink-0" /><span className="line-clamp-2"><span className="text-slate-400 text-[10px] uppercase tracking-wider mr-1">desde:</span>{o.previous_address}</span></div>
          )}
          {o.phone && <div className="flex items-center gap-1.5"><Phone size={12} weight="regular" className="text-slate-400" />{o.phone}</div>}
          {o.email && <div className="flex items-center gap-1.5"><EnvelopeSimple size={12} weight="regular" className="text-slate-400" /><span className="truncate">{o.email}</span></div>}
          {o.plan_name && <div className="flex items-center gap-1.5"><Lightning size={12} weight="regular" className="text-amber-600" /><span className="font-medium text-slate-800">{o.plan_name}</span></div>}
          {o.desired_start_date && <div className="flex items-center gap-1.5"><Calendar size={12} weight="regular" className="text-slate-400" />Inicio: <span className="font-mono">{o.desired_start_date}</span></div>}
          {o.referrer_name && <div className="flex items-center gap-1.5"><Tag size={12} weight="regular" className="text-emerald-600" />Referido por <span className="font-medium text-slate-800">{o.referrer_name}</span></div>}
        </div>

        <div className="flex items-center justify-between pt-3 border-t border-slate-100">
          <div className="text-xs">
            {o.type === "transfer" ? (
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 border border-amber-200 text-amber-700 rounded">TRASLADO</span>
            ) : (
              <span className="text-slate-400 text-[10px] uppercase tracking-wider">Nueva conexión</span>
            )}
          </div>
          <div className="text-[10px] text-slate-400 font-mono">{fmtDate(o.created_at)}</div>
        </div>

        <div className="grid grid-cols-2 gap-2 mt-4">
          <button onClick={() => onApprove(o.id)} className="flex items-center justify-center gap-1.5 py-2 text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-700 transition-colors rounded" data-testid={`approve-${o.id}`}>
            <Check size={14} weight="bold" /> Aprobar
          </button>
          <button onClick={() => onReject(o.id)} className="flex items-center justify-center gap-1.5 py-2 text-xs font-semibold border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors rounded" data-testid={`reject-${o.id}`}>
            <X size={14} weight="bold" /> Rechazar
          </button>
        </div>
      </div>
    </div>
  );
}

/** Compact single-row for processed/historical orders. */
function CompactOrderRow({ o, onDelete }) {
  const isApproved = o.status === "approved";
  return (
    <div
      className="group grid grid-cols-[auto_1fr_auto_auto_auto] items-center gap-3 px-4 py-2.5 border-b border-slate-100 last:border-b-0 hover:bg-slate-50 transition-colors"
      data-testid={`order-row-${o.id}`}
    >
      {/* Status dot */}
      <span
        className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded ${
          isApproved
            ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
            : "bg-slate-100 text-slate-600 border border-slate-200"
        }`}
      >
        {isApproved ? <CheckCircle size={9} weight="fill" /> : <XCircle size={9} weight="fill" />}
        {isApproved ? "Aprobada" : "Rechazada"}
      </span>

      {/* Client + plan */}
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-slate-900 truncate">{o.client_name}</span>
          {o.type === "transfer" && (
            <span className="text-[9px] font-semibold uppercase tracking-wider px-1 py-px border border-amber-200 text-amber-700 rounded">Traslado</span>
          )}
        </div>
        <div className="text-[11px] text-slate-500 flex items-center gap-1.5 mt-0.5 truncate">
          {o.plan_name && <span className="inline-flex items-center gap-1"><Lightning size={9} className="text-amber-500" />{o.plan_name}</span>}
          {o.address && <span className="text-slate-400">· {o.address.split(",")[0]}</span>}
        </div>
      </div>

      {/* ID */}
      <div className="text-[10px] font-mono text-slate-400 uppercase tracking-wider hidden sm:block">
        #{o.id.slice(0, 8).toUpperCase()}
      </div>

      {/* Date */}
      <div className="text-[10px] font-mono text-slate-500 whitespace-nowrap">{fmtDate(o.created_at)}</div>

      {/* Delete */}
      <button
        onClick={() => onDelete(o.id)}
        className="opacity-0 group-hover:opacity-100 text-[10px] text-slate-400 hover:text-red-600 transition-opacity"
        data-testid={`delete-${o.id}`}
        title="Borrar del histórico"
      >
        <X size={13} />
      </button>
    </div>
  );
}

export default function OrdersPage() {
  const [orders, setOrders] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank);
  const [historicFilter, setHistoricFilter] = useState("all"); // all | approved | rejected
  const [historicExpanded, setHistoricExpanded] = useState(true);

  const load = async () => {
    const { data } = await api.get("/support/orders");
    setOrders(data);
  };
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, []);

  const pending = orders.filter(o => o.status === "pending");
  const decided = orders.filter(o => o.status !== "pending");

  const save = async () => {
    try {
      await api.post("/support/orders", { ...form, monthly_fee_estimate: 0 });
      toast.success("Orden creada");
      setOpen(false); setForm(blank); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const approve = async (id) => {
    const order = pending.find(o => o.id === id) || decided.find(o => o.id === id);
    // Pre-flight: surface address history + risk score
    if (order?.address) {
      try {
        const [chkRes, riskRes] = await Promise.all([
          api.get("/clients/check-address", {
            params: { address: order.address, exclude_client_id: order.existing_client_id || "" },
          }),
          api.get("/risk-score", {
            params: {
              address: order.address,
              email: order.email || undefined,
              phone: order.phone || undefined,
              exclude_client_id: order.existing_client_id || undefined,
            },
          }).catch(() => ({ data: null })),
        ]);
        const chk = chkRes.data;
        const risk = riskRes.data;
        const hist = chk.historical_matches || [];
        const active = chk.active_matches || [];
        const hasInfo = active.length || hist.length || (risk && risk.tier !== "LOW");
        if (hasInfo) {
          const lines = [];
          if (risk && risk.tier !== "LOW") {
            lines.push(`Aviso · Riesgo ${risk.tier === "HIGH" ? "ALTO" : "MEDIO"} (score ${risk.score})`);
            for (const s of risk.signals.slice(0, 3)) lines.push(`  • ${s.label}`);
            lines.push("");
          }
          if (active.length) {
            lines.push("Aviso · Dirección YA activa:");
            for (const m of active.slice(0, 3)) lines.push(`  • ${m.client_name} (activo)`);
            lines.push("");
          }
          if (hist.length) {
            lines.push("Historial de la dirección:");
            for (const m of hist.slice(0, 5)) {
              const when = m.disconnected_at
                ? new Date(m.disconnected_at).toLocaleDateString()
                : m.transfer_at
                  ? `traslado ${new Date(m.transfer_at).toLocaleDateString()}`
                  : "—";
              const reason = m.disconnection_reason || m.status;
              lines.push(`  • ${m.client_name} · ${reason} · ${when}`);
            }
          }
          lines.push("\n¿Aprobar la orden de todos modos?");
          if (!confirm(lines.join("\n"))) return;
        } else {
          if (!confirm("¿Aprobar la orden? Se creará un lead asignado para trabajar el contrato.")) return;
        }
      } catch (_) {
        if (!confirm("¿Aprobar la orden? Se creará un lead asignado para trabajar el contrato.")) return;
      }
    } else {
      if (!confirm("¿Aprobar la orden? Se creará un lead asignado para trabajar el contrato.")) return;
    }
    try { await api.post(`/support/orders/${id}/approve`); toast.success("Orden aprobada · lead generado"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const reject = async (id) => {
    const reason = prompt("Razón del rechazo (opcional):");
    if (reason === null) return;
    try { await api.post(`/support/orders/${id}/reject`, { reason }); toast.success("Orden rechazada"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (id) => {
    if (!confirm("¿Borrar esta orden?")) return;
    try { await api.delete(`/support/orders/${id}`); toast.success("Eliminada"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <PageShell
      section="Support" page="Órdenes" context={`${pending.length} pendientes · ${decided.length} procesadas`}
      testid="orders-toolbar"
      actions={<Button onClick={() => setOpen(true)} className="bg-slate-900 hover:bg-slate-800 text-white h-9" data-testid="add-order"><Plus size={15} className="mr-1.5" /> Nueva orden</Button>}
    >
      <div className="space-y-10">
        {/* PENDING — card grid */}
        <section>
          <div className="mb-5 border-b border-slate-200 pb-3 flex items-end justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.24em] text-slate-500 font-semibold">Bandeja</div>
              <h2 className="text-base font-semibold tracking-tight text-slate-900 mt-1.5">Nuevas órdenes</h2>
              <p className="text-[11px] text-slate-500 mt-0.5">Solicitudes pendientes de revisión y aprobación</p>
            </div>
            {pending.length > 0 && (
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2.5 py-1">
                <Clock size={12} weight="bold" /> {pending.length} esperando
              </span>
            )}
          </div>

          {pending.length === 0 ? (
            <EmptyState icon={Briefcase} title="Bandeja vacía" message="Cuando lleguen nuevas órdenes desde el portal financiero o se creen manualmente, aparecerán aquí." compact />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {pending.map(o => <OrderCard key={o.id} o={o} onApprove={approve} onReject={reject} onDelete={remove} />)}
            </div>
          )}
        </section>

        {/* HISTORIC — compact rows, filterable, collapsible */}
        {decided.length > 0 && (() => {
          const filtered = historicFilter === "all"
            ? decided
            : decided.filter(o => o.status === historicFilter);
          const approvedCount = decided.filter(o => o.status === "approved").length;
          const rejectedCount = decided.filter(o => o.status === "rejected").length;
          return (
            <section>
              <div className="mb-4 border-b border-slate-200 pb-3 flex items-end justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.24em] text-slate-500 font-semibold">Histórico</div>
                  <h2 className="text-base font-semibold tracking-tight text-slate-900 mt-1.5 flex items-center gap-2">
                    Órdenes procesadas
                    <span className="text-xs font-mono text-slate-400 font-normal">{decided.length}</span>
                  </h2>
                  <p className="text-[11px] text-slate-500 mt-0.5">Aprobadas y rechazadas en los últimos ciclos</p>
                </div>
                <div className="flex items-center gap-1" data-testid="historic-filters">
                  {[
                    { v: "all", label: "Todo", count: decided.length },
                    { v: "approved", label: "Aprobadas", count: approvedCount },
                    { v: "rejected", label: "Rechazadas", count: rejectedCount },
                  ].map(f => (
                    <button
                      key={f.v}
                      onClick={() => setHistoricFilter(f.v)}
                      data-testid={`hist-filter-${f.v}`}
                      className={`text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1.5 rounded transition-colors flex items-center gap-1.5 ${
                        historicFilter === f.v
                          ? "bg-slate-900 text-white"
                          : "text-slate-500 hover:text-slate-900 hover:bg-slate-100"
                      }`}
                    >
                      {f.label}
                      <span className={`font-mono text-[9px] px-1 rounded ${
                        historicFilter === f.v ? "bg-white/20" : "bg-slate-100"
                      }`}>{f.count}</span>
                    </button>
                  ))}
                  <button
                    onClick={() => setHistoricExpanded(v => !v)}
                    className="ml-1 text-slate-400 hover:text-slate-900 p-1"
                    title={historicExpanded ? "Colapsar" : "Expandir"}
                    data-testid="toggle-historic"
                  >
                    <CaretDown size={13} weight="bold" className={`transition-transform ${historicExpanded ? "" : "-rotate-90"}`} />
                  </button>
                </div>
              </div>

              {historicExpanded && (
                <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden max-h-[500px] overflow-y-auto" data-testid="historic-list">
                  {filtered.length === 0 ? (
                    <div className="text-center text-xs text-slate-400 py-8">
                      Sin órdenes {historicFilter === "approved" ? "aprobadas" : "rechazadas"} en el histórico
                    </div>
                  ) : (
                    filtered.map(o => <CompactOrderRow key={o.id} o={o} onDelete={remove} />)
                  )}
                </div>
              )}
            </section>
          );
        })()}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Nueva orden</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <div className="col-span-2 space-y-1.5"><Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Nombre del cliente *</Label><Input value={form.client_name} onChange={(e) => setForm({...form, client_name: e.target.value})} className={corporateInput} data-testid="order-client" /></div>
            <div className="space-y-1.5"><Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className={corporateInput} /></div>
            <div className="space-y-1.5"><Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Teléfono</Label><Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className={corporateInput} /></div>
            <div className="col-span-2 space-y-1.5"><Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Dirección</Label><Input value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} className={corporateInput} /></div>
            <div className="space-y-1.5"><Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Tipo de servicio</Label><Input value={form.service_type} onChange={(e) => setForm({...form, service_type: e.target.value})} className={corporateInput} /></div>
            <div className="col-span-2 space-y-1.5"><Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Notas</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} className="border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button><Button onClick={save} disabled={!form.client_name} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="save-order">Crear orden</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
}
